import { useState, useEffect } from "react";
import { axiosInstance, API_ENDPOINTS } from "config/api";

/**
 * Custom hook for managing doctors list and related operations
 * Handles fetching doctors based on user role and clinic association
 */
export const useDoctorsList = (isOwner, isAdmin, isDoctor, currentDoctorId, clinicId) => {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [doctorsListData, setDoctorsListData] = useState([]);

  // Fetch doctors list and handle owner/admin/non-owner logic
  useEffect(() => {
    const fetchDoctors = async () => {
      console.log('🔥 === DOCTORS LIST FETCH START ===');
      console.log('🔥 Role info:', { isOwner, isAdmin, isDoctor, currentDoctorId, clinicId });

      try {
        // If user is not an owner or admin (regular doctor), set their own doctor ID
        if (isDoctor && !isOwner && !isAdmin) {
          console.log('🔥 Regular doctor user, setting selectedDoctor to currentDoctorId:', currentDoctorId);
          setSelectedDoctor(currentDoctorId.toString());
            // No need to fetch list for regular doctor in most cases, but we might want them in the list
            // For now, let's skip fetching the full list to save bandwidth unless specifically needed
            // But if we want the "list" to contain just them:
            setDoctors([{ id: currentDoctorId, name: "Current Doctor" }]); // simplified
            return;
          }

          let doctorsList = [];

          // Admin: fetch all doctors
          if (isAdmin) {
            console.log('🔥 Admin user, fetching all doctors');
            // We need a service for "all doctors" if not exists, but for now assuming direct call or use existing endpoint
            // Since we don't have a cached "all doctors" in doctorService yet (only by clinic/profile), 
            // let's stick to existing logic BUT add caching if possible or just be careful.
            // Actually, for admin, it might be heavy. 
            // Ideally we should add fetchAllDoctors to doctorService.
            const response = await axiosInstance.get(API_ENDPOINTS.DOCTOR.ALL);
            doctorsList = Array.isArray(response.data) ? response.data : response.data.doctors || [];
          }
          // Owner: fetch only clinic doctors
          else if (isOwner && clinicId) {
            console.log('🔥 Owner user, fetching clinic doctors for clinic:', clinicId);
            // Use cached service
            const { fetchDoctorsByClinic } = await import('services/doctorService');
            // doctorService returns data directly
            doctorsList = await fetchDoctorsByClinic(clinicId);
          }

          if (doctorsList) {
            // Ensure it's an array
            const validDoctorsList = Array.isArray(doctorsList) ? doctorsList : [];

            console.log('🔥 Fetched doctors list:', validDoctorsList);
            setDoctors(validDoctorsList);
          // Also set the doctors list data for the doctor list tab
            setDoctorsListData(validDoctorsList);

            // For admin users, if no doctor is selected and currentDoctorId exists, 
            // check if currentDoctorId is in the doctors list and auto-select it
            if (isAdmin && !selectedDoctor && currentDoctorId) {
              const currentDoctorExists = validDoctorsList.find(doctor => doctor.id == currentDoctorId);
              if (currentDoctorExists) {
                console.log('🔥 Auto-selecting currentDoctorId for admin user:', currentDoctorId);
                setSelectedDoctor(currentDoctorId.toString());
              }
            }
          }
          console.log('🔥 === DOCTORS LIST FETCH END ===');
        } catch (error) {
          console.error('🔥 Error fetching doctors:', error);
        }
      };
    fetchDoctors();
  }, [isOwner, isAdmin, isDoctor, currentDoctorId, clinicId]);

  return {
    doctors,
    setDoctors,
    selectedDoctor,
    setSelectedDoctor,
    doctorsListData,
    setDoctorsListData
  };
};